/**
 * Group positioned PDF text items into reading-order lines (by Y, then X).
 */

/**
 * @typedef {{ text: string, x: number, y: number, w: number }} PdfTextItem
 */

/**
 * @typedef {{ page: number, y: number, text: string, items: PdfTextItem[] }} PdfLine
 */

/**
 * @param {PdfTextItem[]} items
 * @param {number} tolerance
 * @returns {Array<{ y: number, items: PdfTextItem[] }>}
 */
export function groupItemsByY(items, tolerance = 0.35) {
  /** @type {Array<{ y: number, items: PdfTextItem[] }>} */
  const rows = [];

  for (const item of items) {
    let row = rows.find(existing => Math.abs(existing.y - item.y) <= tolerance);

    if (!row) {
      row = { y: item.y, items: [] };
      rows.push(row);
    }

    row.items.push(item);
  }

  return rows.sort((a, b) => b.y - a.y);
}

/**
 * @param {Record<number, PdfTextItem[]>} pages
 * @returns {PdfLine[]}
 */
export function pdfItemsToLines(pages) {
  /** @type {PdfLine[]} */
  const allLines = [];

  for (const [pageNumber, items] of Object.entries(pages)) {
    const rows = groupItemsByY(items);

    for (const row of rows) {
      const sorted = [...row.items].sort((a, b) => a.x - b.x);
      const text = sorted
        .map(item => item.text)
        .join(' ')
        .trim();

      if (!text) continue;

      allLines.push({
        page: Number(pageNumber),
        y: row.y,
        text,
        items: sorted,
      });
    }
  }

  return allLines;
}
